import { SaveIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { useConfiguratorStore } from "../../input-variable-configurator-store-context";

import { convertToRecord } from "@/components/InputVariableSet/hooks/useInputVariableSetData";
import SaveEntityToLocation from "@/components/SaveEntityToLocation";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/auth-context";
import { EntityType } from "@/queries";
import { useCreateInputVariableSet } from "@/queries/input-variable-sets";

interface SaveInputVariableSetButtonProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  variables: any[];
}

const SaveInputVariableSetButton = observer(({ variables }: SaveInputVariableSetButtonProps) => {
  const auth = useAuth();
  const userToken = auth?.userToken || "";
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const store = useConfiguratorStore();

  const createMutation = useCreateInputVariableSet(userToken);

  const handleExport = async (name: string, folderId: number | null) => {
    await createMutation.mutateAsync({
      name,
      variables: convertToRecord(variables),
      folder_id: folderId,
    });
    setExportDialogOpen(false);
  };

  if (variables.length === 0) {
    return null;
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setExportDialogOpen(true)}
        className="gap-2"
      >
        <SaveIcon className="h-4 w-4" />
        <span className="text-sm">Save as...</span>
      </Button>

      <SaveEntityToLocation
        type={EntityType.INPUT_VARIABLE_SET}
        onSave={handleExport}
        currentFolderId={null}
        defaultName={store.name}
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        title="Save Input Set"
        description="Save this input set to your workspace for reuse across prompts and workflows."
        saveButtonText="Save"
      />
    </>
  );
});

SaveInputVariableSetButton.displayName = "SaveInputVariableSetButton";

export default SaveInputVariableSetButton;
